/**
 * Pure planning for the parent-organization seed, and the name matching the
 * listing's counts share with it.
 *
 * Kept free of Firestore and React so the part that decides which documents get
 * created — and under which ids — is fully testable. The admin button runs this
 * against the LIVE lookup and shows the plan as a dry run before writing a
 * single document.
 */
import type { LookupValue } from '../../types';
import { deriveBrandSlug } from '../../services/brandSlug';

/**
 * Slug a legacy parent-org value into a `parent_organizations` doc id.
 *
 * The brand convention (`deriveBrandSlug`: lowercase, hyphens, alphanumerics),
 * with ONE addition: underscores are word breaks first. The lookup holds two
 * generations of value — display strings from the original seeds
 * (`Swatch Group`, `LVMH`) and underscore keys minted by the Lookup Manager
 * (`rolex_sa`, `dartmouth_brands`). `deriveBrandSlug` strips characters outside
 * `[a-z0-9\\s-]`, so on its own `rolex_sa` would collapse to `rolexsa`; treating
 * `_` as a space first gives `rolex-sa`, which is what both generations should
 * agree on.
 */
export function slugifyParentOrgValue(value: string): string {
  return deriveBrandSlug((value ?? '').replace(/_/g, ' ')).replace(/-{2,}/g, '-');
}

/**
 * Case- and spacing-insensitive comparison key for a parent-organization NAME —
 * used only to decide whether two spellings name the same organization.
 *
 * It follows the id slug's rules ({@link slugifyParentOrgValue}: underscores
 * are spaces, punctuation is dropped, spaces become hyphens) with ONE
 * difference: letters and digits of every script are kept. The slug is
 * ASCII-only because it mints document ids, so it erased every non-Latin name
 * to an empty string — `孔雀表（集团）有限公司`, Peacock Watch Group's Chinese
 * legal name, could never match the organization that lists it as an
 * alternative name, and showed in listings as an unmatched parent with a filter
 * chip of its own.
 *
 * For a plain-ASCII name the result is identical to the slug (`rolex_sa`,
 * `Rolex SA` and `Rolex S.A.` all give `rolex-sa`), so every match that held
 * before still holds. Accented Latin letters now fold onto their base letter
 * (`Moët` → `moet`) instead of vanishing (`mot`), which only adds matches.
 */
export function parentOrgMatchKey(value: string): string {
  return (value ?? '')
    .replace(/_/g, ' ')
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Lookup values the design pass flagged as probably NOT parent organizations
 * under the working definition ("a legal entity that OWNS brands or
 * manufacturers as portfolio assets"). Sellita is a movement supplier; Magrette
 * and Pointtec are brands. The seed defaults these to SKIP, so creating one is
 * an explicit per-entry decision rather than something that happens because
 * the button was pressed.
 */
export const SUSPECTED_MISCLASSIFIED: Readonly<Record<string, string>> = {
  'sellita-watch-co-s-a': 'Sellita is a movement supplier, not a parent organization.',
  magrette: 'Magrette is a watch brand, not a parent organization.',
  pointtec: 'Pointtec is a watch brand, not a parent organization.',
  'korius-group': 'Flagged in the design pass as a possible misclassification — check before seeding.',
};

/** One document the seed would create. */
export interface ParentOrgSeedEntry {
  /** Slug doc id. */
  id: string;
  name: string;
  displayName: string;
  description?: string;
  /**
   * Every legacy string this org has been known by — the raw lookup `value`(s)
   * and display name(s). Phase 3's backfill matches brand and manufacturer
   * `parentOrg` strings against these, which is why they must be preserved
   * verbatim rather than normalised.
   */
  alternativeNames: string[];
  /** The lookup entries folded into this one — more than one means a merge. */
  sourceValues: string[];
  /** Why this entry defaults to skip, when it does. */
  suspectedMisclassification?: string;
}

export interface ParentOrgSeedPlan {
  /** Would be created. Includes suspected misclassifications, which default to skipped. */
  toCreate: ParentOrgSeedEntry[];
  /** Already has a document — left completely alone. */
  alreadyExists: ParentOrgSeedEntry[];
  /** Lookup entries with no usable value/name — cannot be slugged. */
  unusable: LookupValue[];
  /**
   * `parentOrg` strings actually used on brand or manufacturer documents that
   * match NO lookup entry. Not seeded (the lookup is the seed source), but Phase
   * 3's backfill has to account for every one of them.
   */
  usedButNotInLookup: Array<{ value: string; brands: number; manufacturers: number }>;
}

/** Usage counts of one raw `parentOrg` string across the two entity indexes. */
export interface ParentOrgUsage {
  brands: Record<string, number>;
  manufacturers: Record<string, number>;
}

function uniqueNonEmpty(values: Array<string | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    const v = (raw ?? '').trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

/**
 * Plan the seed from the lookup's values.
 *
 * - One document per SLUG. Two lookup entries that slug to the same id (a
 *   display-string seed and its later underscore twin) merge: first entry's
 *   name and description win, every raw value lands in `alternativeNames`.
 * - Inactive lookup values are included — an org that is no longer offered in
 *   the picker still owns the brands that point at it.
 * - An id with an existing document goes to `alreadyExists` and is never
 *   written: the seed only ever creates.
 */
export function planParentOrgSeed(
  lookupValues: readonly LookupValue[],
  existingIds: ReadonlySet<string>,
  usage: ParentOrgUsage = { brands: {}, manufacturers: {} },
): ParentOrgSeedPlan {
  const byId = new Map<string, ParentOrgSeedEntry>();
  const unusable: LookupValue[] = [];

  for (const lv of lookupValues) {
    const raw = (lv?.value || lv?.displayName || '').trim();
    const id = slugifyParentOrgValue(raw);
    if (!id) {
      unusable.push(lv);
      continue;
    }
    const displayName = (lv.displayName || lv.value || '').trim();
    const existing = byId.get(id);
    if (existing) {
      existing.alternativeNames = uniqueNonEmpty([
        ...existing.alternativeNames,
        lv.value,
        lv.displayName,
      ]);
      existing.sourceValues = uniqueNonEmpty([...existing.sourceValues, lv.value]);
      if (!existing.description && lv.description?.trim()) {
        existing.description = lv.description.trim();
      }
      continue;
    }
    const entry: ParentOrgSeedEntry = {
      id,
      name: displayName,
      displayName,
      alternativeNames: uniqueNonEmpty([lv.value, lv.displayName]),
      sourceValues: uniqueNonEmpty([lv.value]),
    };
    if (lv.description?.trim()) entry.description = lv.description.trim();
    if (SUSPECTED_MISCLASSIFIED[id]) {
      entry.suspectedMisclassification = SUSPECTED_MISCLASSIFIED[id];
    }
    byId.set(id, entry);
  }

  const toCreate: ParentOrgSeedEntry[] = [];
  const alreadyExists: ParentOrgSeedEntry[] = [];
  for (const entry of byId.values()) {
    (existingIds.has(entry.id) ? alreadyExists : toCreate).push(entry);
  }
  const byName = (a: ParentOrgSeedEntry, b: ParentOrgSeedEntry) =>
    a.displayName.localeCompare(b.displayName);
  toCreate.sort(byName);
  alreadyExists.sort(byName);

  // A used string is "in the lookup" when it slugs to any seeded id — the same
  // key the listing's counts resolve through.
  const knownKeys = new Set(byId.keys());
  const usedValues = new Set([
    ...Object.keys(usage.brands),
    ...Object.keys(usage.manufacturers),
  ]);
  const usedButNotInLookup = [...usedValues]
    .filter((value) => !knownKeys.has(parentOrgMatchKey(value)))
    .map((value) => ({
      value,
      brands: usage.brands[value] ?? 0,
      manufacturers: usage.manufacturers[value] ?? 0,
    }))
    .sort((a, b) => b.brands + b.manufacturers - (a.brands + a.manufacturers));

  return { toCreate, alreadyExists, unusable, usedButNotInLookup };
}

/**
 * How many brands and manufacturers point at an organization, by its legacy
 * strings.
 *
 * Brand and manufacturer docs still carry the free-text `parentOrg` (Phase 3
 * swaps it for `parentOrgId`), so a count has to match that string. An org
 * claims a string when it slugs to the org's id or to any of its alternative
 * names — which is what makes `rolex_sa` and `Rolex SA` both count for
 * `rolex-sa`.
 */
export function countParentOrgUsage(
  org: { id: string; name?: string; displayName?: string; alternativeNames?: string[] },
  usage: ParentOrgUsage,
): { brands: number; manufacturers: number } {
  const keys = new Set(
    [org.id, org.name, org.displayName, ...(org.alternativeNames ?? [])]
      .map((v) => parentOrgMatchKey(v ?? ''))
      .filter(Boolean),
  );
  const sum = (counts: Record<string, number>) =>
    Object.entries(counts).reduce(
      (total, [value, n]) => (keys.has(parentOrgMatchKey(value)) ? total + n : total),
      0,
    );
  return { brands: sum(usage.brands), manufacturers: sum(usage.manufacturers) };
}

/**
 * Every ancestor-or-self id an org may NOT take as its parent: itself and every
 * organization below it. Choosing one of those would make the tree a cycle.
 */
export function forbiddenParentIds(
  orgId: string,
  orgs: ReadonlyArray<{ id: string; parentOrgId?: string }>,
): Set<string> {
  const children = new Map<string, string[]>();
  for (const o of orgs) {
    if (!o.parentOrgId) continue;
    const list = children.get(o.parentOrgId) ?? [];
    list.push(o.id);
    children.set(o.parentOrgId, list);
  }
  const forbidden = new Set<string>([orgId]);
  const stack = [orgId];
  while (stack.length) {
    const current = stack.pop()!;
    for (const child of children.get(current) ?? []) {
      if (forbidden.has(child)) continue;
      forbidden.add(child);
      stack.push(child);
    }
  }
  return forbidden;
}
